import * as Yup from 'yup';

import { shouldRequirePrevious } from '../../components/monthsAndYears/MonthsAndYears';
import { OWNERSHIP_TYPE } from '../../constants/addresses';
import { Country, EmploymentStatusEnum } from '../../gql/generated/graphql';
import { notRequiredString } from '../strings';
import {
  addressLine2Validation,
  addressLineValidation,
  cityValidation,
  countryCountyValidation,
  stateValidation,
} from './address';
import { emailValidationRequired } from './email';
import {
  employerNameValidation,
  employmentStatusValidation,
  jobTitleValidation,
} from './employment';
import { nameValidationMax30 } from './names';
import {
  monthsValidation,
  numberOptionalValidation,
  numberRequiredValidation,
  salaryRequiredAndMaxValidation,
} from './numbers';
import { phoneValidationRequired } from './phoneNumber';
import { residenceTypeValidation, residenceYearsValidation } from './residence';
import { postalCodeValidationByCountry } from './zipCodes';

// Personal Information
export const cobuyerNameValidation = {
  cobuyer_first_name: nameValidationMax30,
  cobuyer_last_name: nameValidationMax30,
};

export const cobuyerContactInfoValidation = {
  cobuyer_email: emailValidationRequired,
  cobuyer_phone_number: phoneValidationRequired,
};
export const cobuyerPersonalInfoValidation = {
  ...cobuyerNameValidation,
  ...cobuyerContactInfoValidation,
};

// Address
export const cobuyerPrimaryAddressValidation = (country: Country) => ({
  cobuyer_address_line: addressLineValidation,
  cobuyer_address_line_2: addressLine2Validation,
  cobuyer_city: cityValidation,
  cobuyer_state: stateValidation,
  cobuyer_county: countryCountyValidation(country),
  cobuyer_zip: postalCodeValidationByCountry[country],
});
// Residence
export const cobuyerPrimaryResidenceValidation = {
  cobuyer_primary_residence_type: residenceTypeValidation,
  cobuyer_primary_residence_years: residenceYearsValidation,
  cobuyer_primary_residence_months: Yup.number().when('cobuyer_primary_residence_years', {
    is: (cobuyer_primary_residence_years: number) => cobuyer_primary_residence_years <= 1,
    then: monthsValidation,
    otherwise: numberOptionalValidation,
  }),
  cobuyer_primary_residence_monthly_payment: Yup.number().when('cobuyer_primary_residence_type', {
    is: (cobuyer_primary_residence_type: string) =>
      cobuyer_primary_residence_type !== OWNERSHIP_TYPE.OWN_HOME_OUTRIGHT,
    then: numberRequiredValidation,
    otherwise: numberOptionalValidation,
  }),
};

export const cobuyerPreviousResidenceValidation = (country: Country) => ({
  cobuyer_secondary_residence_years: Yup.number().when(
    ['cobuyer_primary_residence_years', 'cobuyer_primary_residence_months'],
    {
      is: shouldRequirePrevious,
      then: residenceYearsValidation,
      otherwise: numberOptionalValidation,
    },
  ),
  cobuyer_secondary_residence_months: Yup.number().when('cobuyer_primary_residence_years', {
    is: (cobuyer_primary_residence_years: number) => cobuyer_primary_residence_years === 0,
    then: monthsValidation,
    otherwise: numberOptionalValidation,
  }),
  cobuyer_previous_address_line: Yup.string().when(
    ['cobuyer_primary_residence_years', 'cobuyer_primary_residence_months'],
    {
      is: shouldRequirePrevious,
      then: addressLineValidation,
      otherwise: notRequiredString,
    },
  ),
  cobuyer_previous_address_line_2: Yup.string().when(
    ['cobuyer_primary_residence_years', 'cobuyer_primary_residence_months'],
    {
      is: shouldRequirePrevious,
      then: addressLine2Validation,
      otherwise: notRequiredString,
    },
  ),
  cobuyer_previous_city: Yup.string().when(
    ['cobuyer_primary_residence_years', 'cobuyer_primary_residence_months'],
    {
      is: shouldRequirePrevious,
      then: cityValidation,
      otherwise: notRequiredString,
    },
  ),
  cobuyer_previous_state: Yup.string().when(
    ['cobuyer_primary_residence_years', 'cobuyer_primary_residence_months'],
    {
      is: shouldRequirePrevious,
      then: stateValidation,
      otherwise: notRequiredString,
    },
  ),
  cobuyer_previous_zip: Yup.string().when(
    ['cobuyer_primary_residence_years', 'cobuyer_primary_residence_months'],
    {
      is: shouldRequirePrevious,
      then: postalCodeValidationByCountry[country],
      otherwise: notRequiredString,
    },
  ),
  cobuyer_previous_county: Yup.string().when(
    ['cobuyer_primary_residence_years', 'cobuyer_primary_residence_months'],
    {
      is: shouldRequirePrevious,
      then: countryCountyValidation(country),
      otherwise: notRequiredString,
    },
  ),
});

// Employment
export const cobuyerEmploymentValidation = {
  cobuyer_employment_status: employmentStatusValidation,
  cobuyer_employer_name: Yup.string().when('cobuyer_employment_status', {
    is: (cobuyer_employment_status: EmploymentStatusEnum) =>
      cobuyer_employment_status === EmploymentStatusEnum.Employed,
    then: employerNameValidation,
    otherwise: notRequiredString,
  }),
  cobuyer_employer_phone_number: Yup.string().when('cobuyer_employment_status', {
    is: (cobuyer_employment_status: EmploymentStatusEnum) =>
      cobuyer_employment_status === EmploymentStatusEnum.Employed,
    then: phoneValidationRequired,
    otherwise: notRequiredString,
  }),
  cobuyer_job_title: Yup.string().when('cobuyer_employment_status', {
    is: (cobuyer_employment_status: EmploymentStatusEnum) =>
      cobuyer_employment_status === EmploymentStatusEnum.Employed,
    then: jobTitleValidation,
    otherwise: notRequiredString,
  }),
  cobuyer_years_at_job: numberRequiredValidation,
  cobuyer_months_at_job: Yup.number().when('cobuyer_years_at_job', {
    is: (cobuyer_years_at_job: number) => cobuyer_years_at_job <= 1,
    then: monthsValidation,
    otherwise: numberOptionalValidation,
  }),
  cobuyer_salary: salaryRequiredAndMaxValidation,
};

export const cobuyerPreviousEmploymentValidation = {
  cobuyer_previous_employer_name: Yup.string().when(
    ['cobuyer_years_at_job', 'cobuyer_months_at_job'],
    {
      is: shouldRequirePrevious,
      then: employerNameValidation,
      otherwise: notRequiredString,
    },
  ),
  cobuyer_previous_employer_phone_number: Yup.string().when(
    ['cobuyer_years_at_job', 'cobuyer_months_at_job'],
    {
      is: shouldRequirePrevious,
      then: phoneValidationRequired,
      otherwise: notRequiredString,
    },
  ),
  cobuyer_previous_job_title: Yup.string().when(['cobuyer_years_at_job', 'cobuyer_months_at_job'], {
    is: (cobuyer_years_at_job: number, cobuyer_months_at_job: number) =>
      cobuyer_years_at_job === 0 || (cobuyer_years_at_job === 1 && cobuyer_months_at_job < 8),
    then: jobTitleValidation,
    otherwise: notRequiredString,
  }),
  cobuyer_previous_years_at_job: Yup.number().when(
    ['cobuyer_years_at_job', 'cobuyer_months_at_job'],
    {
      is: shouldRequirePrevious,
      then: numberRequiredValidation,
      otherwise: numberOptionalValidation,
    },
  ),
  cobuyer_previous_months_at_job: Yup.number().when('cobuyer_previous_years_at_job', {
    is: (cobuyer_previous_years_at_job: number) => cobuyer_previous_years_at_job === 0,
    then: monthsValidation,
    otherwise: numberOptionalValidation,
  }),
};
